/**
 * Profile page — User's personal profile and notification preferences.
 * Moved from NotificationSettings, now serves as the user-facing profile page.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../lib/api';
import AppLayout from '../components/layout/AppLayout';
import { User, Bell, Shield, Hash, Truck, KeyRound } from 'lucide-react';
import { ALL_STATIONS, BATTALIONS, type UnitDef } from '../../../shared/roster-config';

interface Preferences {
  swap_requests: boolean;
  swap_updates: boolean;
  schedule_changes: boolean;
}

interface PreferencesResponse {
  preferences: Preferences;
  active_subscriptions: number;
}

export default function Profile() {
  const { user } = useAuth();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  // Fetch profile photo from Microsoft Graph via our proxy
  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    fetch('/api/auth/photo', { credentials: 'include', signal: controller.signal })
      .then((res) => {
        if (res.ok) return res.blob();
        return null;
      })
      .then((blob) => {
        if (blob && blob.size > 0) setPhotoUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        /* Photo loading is best-effort */
      });
    return () => {
      controller.abort();
    };
  }, [user]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
  }, [photoUrl]);

  const [prefs, setPrefs] = useState<Preferences>({
    swap_requests: true,
    swap_updates: true,
    schedule_changes: true,
  });
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pushSupported, setPushSupported] = useState(false);

  // Badge number (read-only, sourced from Airtable)
  const [badgeNumber, setBadgeNumber] = useState<string | null>(null);

  // PWC Username (self-editable, used by officers for MDC login)
  const [pwcUsername, setPwcUsername] = useState('');
  const [pwcDraft, setPwcDraft] = useState('');
  const [pwcSaving, setPwcSaving] = useState(false);

  useEffect(() => {
    setPushSupported('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window);
  }, []);

  // Fetch badge number and PWC username
  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<{ badge_number: string | null; pwc_username: string | null }>('/api/profile/badge');
        setBadgeNumber(data.badge_number);
        setPwcUsername(data.pwc_username ?? '');
        setPwcDraft(data.pwc_username ?? '');
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<PreferencesResponse>('/api/notifications/preferences');
        setPrefs(data.preferences);
        setSubscriptionCount(data.active_subscriptions);
        setIsSubscribed(data.active_subscriptions > 0);
      } catch {
        // defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleTogglePref = useCallback(
    async (key: keyof Preferences) => {
      setError(null);
      setSuccess(null);
      setSaving(true);
      const newValue = !prefs[key];
      const updatedPrefs = { ...prefs, [key]: newValue };
      setPrefs(updatedPrefs);
      try {
        await apiFetch('/api/notifications/preferences', {
          method: 'PUT',
          body: JSON.stringify({ [key]: newValue }),
        });
        setSuccess('Preference saved');
        setTimeout(() => setSuccess(null), 1500);
      } catch (err) {
        setPrefs(prefs);
        setError(err instanceof Error ? err.message : 'Failed to save preference');
      } finally {
        setSaving(false);
      }
    },
    [prefs],
  );

  const handleSavePwc = useCallback(async () => {
    setError(null);
    setSuccess(null);
    setPwcSaving(true);
    const trimmed = pwcDraft.trim();
    const prev = pwcUsername;
    setPwcUsername(trimmed);
    try {
      const data = await apiFetch<{ pwc_username: string | null }>('/api/profile/pwc-username', {
        method: 'PUT',
        body: JSON.stringify({ pwc_username: trimmed }),
      });
      const saved = data.pwc_username ?? '';
      setPwcUsername(saved);
      setPwcDraft(saved);
      setSuccess('PWC Username saved');
      setTimeout(() => setSuccess(null), 1500);
    } catch (err) {
      setPwcUsername(prev);
      setPwcDraft(prev);
      setError(err instanceof Error ? err.message : 'Failed to save PWC Username');
    } finally {
      setPwcSaving(false);
    }
  }, [pwcDraft, pwcUsername]);

  const handleSubscribe = useCallback(async () => {
    setError(null);
    setSuccess(null);
    if (!pushSupported) {
      setError('Push notifications are not supported in this browser');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Notification permission denied.');
        return;
      }
      let vapidKey: string;
      try {
        const r = await apiFetch<{ vapid_public_key: string }>('/api/notifications/vapid-key');
        vapidKey = r.vapid_public_key;
      } catch {
        setError('Push not configured on server.');
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as BufferSource,
      });
      const subJson = subscription.toJSON();
      await apiFetch('/api/notifications/subscribe', {
        method: 'POST',
        body: JSON.stringify({ endpoint: subJson.endpoint, keys: subJson.keys }),
      });
      setIsSubscribed(true);
      setSubscriptionCount((c) => c + 1);
      setSuccess('Push notifications enabled for this device');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable push notifications');
    }
  }, [pushSupported]);

  const handleUnsubscribe = useCallback(async () => {
    setError(null);
    setSuccess(null);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await apiFetch('/api/notifications/subscribe', {
          method: 'DELETE',
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
      setSubscriptionCount((c) => Math.max(0, c - 1));
      setSuccess('Push notifications disabled for this device');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable push notifications');
    }
  }, []);

  if (loading) {
    return (
      <AppLayout title="Profile">
        <div className="min-h-[50dvh] flex items-center justify-center text-content-tertiary text-base">Loading...</div>
      </AppLayout>
    );
  }

  const userRole = user?.role ?? 'member';

  return (
    <AppLayout title="Profile">
      <div className="w-full max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-content-primary mt-4 mb-6">My Profile</h1>

        {error && (
          <div className="mb-4 px-3 py-2 text-[13px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-md">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 px-3 py-2 text-[13px] text-green-400 bg-green-500/10 border border-green-500/30 rounded-md">
            {success}
          </div>
        )}

        {/* User Info Card */}
        <div className="p-5 bg-surface-raised rounded-xl border border-edge-default mb-5">
          <div className="flex items-center gap-4 mb-4">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt=""
                className="w-14 h-14 rounded-full object-cover border-2 border-edge-muted shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-action-primary/20 border-2 border-edge-muted flex items-center justify-center shrink-0">
                <User className="h-7 w-7 text-action-primary" />
              </div>
            )}
            <div>
              <div className="text-lg font-bold text-content-primary">{user?.name ?? ''}</div>
              <div className="text-sm text-content-secondary">{user?.email ?? ''}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <InfoField icon={<Shield className="h-3.5 w-3.5" />} label="Role" value={userRole} />
            <InfoField
              icon={<User className="h-3.5 w-3.5" />}
              label="Battalion"
              value={user?.battalion?.id ? `Battalion ${user.battalion.id}` : 'Unassigned'}
            />
          </div>
          {user?.assignment && (
            <div className="mt-3 pt-3 border-t border-edge-muted">
              <div className="flex items-center gap-1.5 text-[11px] text-content-tertiary uppercase tracking-wide mb-2">
                <Truck className="h-3.5 w-3.5" />
                Roster Assignment
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InfoField
                  icon={<Truck className="h-3.5 w-3.5" />}
                  label="Unit"
                  value={resolveUnitLabel(user.assignment.unit_id)}
                />
                <InfoField
                  icon={<User className="h-3.5 w-3.5" />}
                  label="Position"
                  value={resolveSeatLabel(user.assignment.unit_id, user.assignment.seat_id)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Badge Number (CO. ID) — read-only, sourced from Airtable */}
        <div className="p-5 bg-surface-raised rounded-xl border border-edge-default mb-5">
          <div className="flex items-center gap-2 mb-2">
            <Hash className="h-4 w-4 text-action-primary" />
            <div className="text-[15px] font-bold text-content-primary">Badge Number (CO. ID)</div>
          </div>
          {badgeNumber ? (
            <div className="text-lg font-bold text-content-primary">{badgeNumber}</div>
          ) : (
            <div className="text-[13px] text-content-tertiary">
              Not set. This is synced automatically from Airtable. Contact an admin if your CO. ID is missing.
            </div>
          )}
        </div>

        {/* PWC Username — self-editable, used by officers for MDC login */}
        <div className="p-5 bg-surface-raised rounded-xl border border-edge-default mb-5">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="h-4 w-4 text-action-primary" />
            <div className="text-[15px] font-bold text-content-primary">PWC Username</div>
          </div>
          <div className="text-xs text-content-tertiary mb-3">
            Used by officers when logging riders into the MDC. Set your own username here.
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={pwcDraft}
              onChange={(e) => setPwcDraft(e.target.value)}
              placeholder="e.g. rjb101808v"
              maxLength={50}
              disabled={pwcSaving}
              className="flex-1 bg-surface-sunken border border-edge-muted rounded-lg px-3 py-2 text-sm text-content-primary placeholder:text-content-disabled outline-none focus:border-action-primary transition-colors duration-150 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleSavePwc}
              disabled={pwcSaving || pwcDraft.trim() === pwcUsername}
              className="px-4 py-2 text-sm font-semibold border-none rounded-lg cursor-pointer bg-action-primary text-content-on-primary disabled:opacity-50 disabled:cursor-default transition-colors duration-150"
            >
              {pwcSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Push Notifications */}
        <div className="p-5 bg-surface-raised rounded-xl border border-edge-default mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="h-4 w-4 text-action-primary" />
            <div className="text-[15px] font-bold text-content-primary">Push Notifications</div>
          </div>
          <div className="text-[13px] text-content-secondary mb-3">
            {pushSupported
              ? isSubscribed
                ? `Enabled on this device. ${subscriptionCount} active subscription${subscriptionCount !== 1 ? 's' : ''}.`
                : 'Not enabled on this device. Subscribe to receive real-time alerts.'
              : 'Push notifications are not supported in this browser.'}
          </div>
          <button
            type="button"
            onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
            disabled={!pushSupported}
            className={`px-5 py-2.5 text-sm font-semibold border-none rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-default transition-colors duration-150 ${
              isSubscribed
                ? 'bg-surface-sunken text-content-primary hover:bg-action-secondary-hover'
                : 'bg-action-primary text-content-on-primary'
            }`}
          >
            {isSubscribed ? 'Disable Notifications' : 'Enable Notifications'}
          </button>
        </div>

        {/* Notification Preferences */}
        <div className="mb-5">
          <div className="text-[13px] font-semibold text-content-tertiary uppercase tracking-wider mb-3">
            Notify Me About
          </div>
          {[
            {
              key: 'swap_requests' as const,
              label: 'Incoming Swap Requests',
              desc: 'When someone wants to swap a shift with you',
            },
            {
              key: 'swap_updates' as const,
              label: 'Swap Status Updates',
              desc: 'When your swap request is approved or rejected',
            },
            { key: 'schedule_changes' as const, label: 'Schedule Changes', desc: 'When the duty schedule is modified' },
          ].map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between p-3.5 bg-surface-raised rounded-xl border border-edge-default mb-2"
            >
              <div className="flex-1 mr-3">
                <div className="text-sm font-semibold text-content-primary">{item.label}</div>
                <div className="text-xs text-content-tertiary mt-0.5">{item.desc}</div>
              </div>
              <button
                type="button"
                onClick={() => handleTogglePref(item.key)}
                disabled={saving}
                className={`relative w-11 h-6 rounded-full border-none shrink-0 transition-colors duration-200 ${saving ? 'cursor-default' : 'cursor-pointer'} ${prefs[item.key] ? 'bg-green-500' : 'bg-surface-sunken'}`}
                aria-label={`${item.label}: ${prefs[item.key] ? 'enabled' : 'disabled'}`}
              >
                <div
                  className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-[left] duration-200 ${prefs[item.key] ? 'left-[23px]' : 'left-[3px]'}`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}

function InfoField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-surface-sunken rounded-lg px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] text-content-tertiary uppercase tracking-wide mb-0.5">
        {icon}
        {label}
      </div>
      <div className="text-sm font-semibold text-content-primary capitalize">{value}</div>
    </div>
  );
}

/** Look up a unit definition by ID across all stations and command units */
function findUnit(unitId: string): UnitDef | undefined {
  for (const station of ALL_STATIONS) {
    const unit = station.units.find((u) => u.id === unitId);
    if (unit) return unit;
  }
  for (const bn of BATTALIONS) {
    const unit = bn.commandUnits.find((u) => u.id === unitId);
    if (unit) return unit;
  }
  return undefined;
}

function resolveUnitLabel(unitId: string): string {
  return findUnit(unitId)?.label ?? unitId;
}

function resolveSeatLabel(unitId: string, seatId: string): string {
  const unit = findUnit(unitId);
  if (!unit) return seatId;
  const seat = unit.seats.find((s) => s.id === seatId);
  return seat?.label ?? seatId;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
